import { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import Accordion from "components/shared/accordion/Accordion";
import Pagination from "components/shared/pagination/Pagination";
import { physicalStores } from "helpers/stores";
import StoreInformation from "../storeInformation/StoreInformation";
import { Skeleton, Label, Logo, AccordionWrapper } from "./StoresList.styled";

function StoresList({
    errorMessage,
    stores,
    activeItem,
    setActiveItem,
    map,
    placesService
}) {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const perPage = 6;

    const showStoresInCurrentPage = useCallback(() => {
        if (!stores || stores.length === 0) {
            return [];
        }

        const offset = currentPageIndex * perPage;
        return stores.slice(offset, offset + perPage);
    }, [stores, currentPageIndex]);

    useEffect(() => {
        if (!activeItem || !stores) {
            return;
        }
        const index = stores.findIndex(store => store.place_id === activeItem);
        const pageLocation = Math.ceil((index + 1) / perPage) - 1;
        setCurrentPageIndex(pageLocation);
    }, [stores, activeItem]);

    if (errorMessage) {
        return <div>{errorMessage}</div>;
    }

    if (!stores) {
        return <Skeleton />;
    }

    if (stores.length === 0) {
        return <div>Geen locaties in jouw omgeving gevonden.</div>;
    }

    return (
        <>
            <AccordionWrapper>
                {showStoresInCurrentPage().map(store => {
                    const currentBrand = physicalStores.find(storeBrand => {
                        const storeName = store?.name?.toLowerCase();
                        const brandName = storeBrand?.id?.toLowerCase();
                        return storeName.includes(brandName);
                    });

                    const storeImage = currentBrand?.image?.color;

                    return (
                        <Accordion
                            key={store.place_id}
                            title={
                                <Label>
                                    {storeImage && (
                                        <Logo
                                            src={storeImage}
                                            alt={`${store.name} logo`}
                                        />
                                    )}{" "}
                                    {store.name}
                                </Label>
                            }
                            variant="minimal"
                            active={activeItem === store.place_id}
                            onExpand={() => setActiveItem(store.place_id)}
                            onCollapse={() => setActiveItem(null)}
                            onClick={() => {
                                map.panTo(store.geometry.location);
                            }}
                        >
                            {activeItem === store.place_id && (
                                <StoreInformation
                                    activeItem={activeItem}
                                    placesService={placesService}
                                />
                            )}
                        </Accordion>
                    );
                })}
            </AccordionWrapper>
            {stores?.length > perPage && (
                <Pagination
                    amountOfItems={stores?.length}
                    perPage={perPage}
                    currentPageIndex={currentPageIndex}
                    setCurrentPageIndex={setCurrentPageIndex}
                />
            )}
        </>
    );
}

StoresList.propTypes = {
    errorMessage: PropTypes.string,
    stores: PropTypes.array,
    activeItem: PropTypes.string,
    setActiveItem: PropTypes.func.isRequired,
    map: PropTypes.object,
    placesService: PropTypes.object
};

StoresList.defaultProps = {
    errorMessage: null,
    stores: [],
    activeItem: null,
    map: null,
    placesService: null
};

export default StoresList;
